import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Eraser } from "lucide-react";
import api from "@/lib/api";
import { Section } from "./common";
import { TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const QUICK_BALLS = ["0", "1", "2", "3", "4", "6", "W", "WD", "NB"];
const EMPTY_INN = () => ({ label: "", runs: 0, wickets: 0, overs: "" });

export default function LiveTab() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [match, setMatch] = useState(null);
  const [customBall, setCustomBall] = useState("");
  const [form, setForm] = useState({ team_a: "", team_b: "", status_text: "", stream_url: "", active: false, innings: [EMPTY_INN(), EMPTY_INN()] });

  const load = async () => {
    try {
      const { data } = await api.get("/live/match");
      if (data.exists !== false) {
        setMatch(data);
        const inn = [...(data.innings || [])];
        while (inn.length < 2) inn.push(EMPTY_INN());
        setForm({ team_a: data.team_a || "", team_b: data.team_b || "", status_text: data.status_text || "", stream_url: data.stream_url || "", active: !!data.active, innings: inn.slice(0, 2) });
      }
    } catch {
      toast.error("Couldn't load live match");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setInn = (i, key, val) =>
    setForm((f) => ({ ...f, innings: f.innings.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)) }));

  const save = async () => {
    if (!form.team_a.trim() || !form.team_b.trim()) {
      toast.error("Enter both team names");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        innings: form.innings
          .filter((r) => r.label.trim())
          .map((r) => ({ ...r, runs: Number(r.runs) || 0, wickets: Number(r.wickets) || 0, overs: r.overs || "0.0" })),
      };
      const { data } = await api.put("/live/match", payload);
      setMatch(data);
      toast.success(form.active ? "Scoreboard is LIVE" : "Scoreboard saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const addBall = async (ball) => {
    try {
      const { data } = await api.post("/live/ball", { ball });
      setMatch(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add ball");
    }
  };

  const clearBalls = async () => {
    try {
      const { data } = await api.delete("/live/balls");
      setMatch(data);
      toast.success("Ball log cleared");
    } catch {
      toast.error("Clear failed");
    }
  };

  if (loading) return <TabsContent value="live"><p className="text-muted-foreground">Loading…</p></TabsContent>;

  return (
    <TabsContent value="live" data-testid="admin-live-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Match setup & score">
          <div className="space-y-4" data-testid="admin-live-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="live-team-a">Team A</Label>
                <Input id="live-team-a" value={form.team_a} onChange={(e) => setForm({ ...form, team_a: e.target.value })} className="bg-white" data-testid="admin-live-team-a" />
              </div>
              <div>
                <Label htmlFor="live-team-b">Team B</Label>
                <Input id="live-team-b" value={form.team_b} onChange={(e) => setForm({ ...form, team_b: e.target.value })} className="bg-white" data-testid="admin-live-team-b" />
              </div>
            </div>
            {[0, 1].map((i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-5">
                  <Label>Batting side {i + 1}</Label>
                  <Input value={form.innings[i].label} onChange={(e) => setInn(i, "label", e.target.value)} placeholder="e.g. Surat Strikers" className="bg-white" data-testid={`admin-live-inn-label-${i}`} />
                </div>
                <div className="col-span-2">
                  <Label>Runs</Label>
                  <Input type="number" min="0" value={form.innings[i].runs} onChange={(e) => setInn(i, "runs", e.target.value)} className="bg-white" data-testid={`admin-live-inn-runs-${i}`} />
                </div>
                <div className="col-span-2">
                  <Label>Wkts</Label>
                  <Input type="number" min="0" max="10" value={form.innings[i].wickets} onChange={(e) => setInn(i, "wickets", e.target.value)} className="bg-white" data-testid={`admin-live-inn-wickets-${i}`} />
                </div>
                <div className="col-span-3">
                  <Label>Overs</Label>
                  <Input value={form.innings[i].overs} onChange={(e) => setInn(i, "overs", e.target.value)} placeholder="12.3" className="bg-white" data-testid={`admin-live-inn-overs-${i}`} />
                </div>
              </div>
            ))}
            <div>
              <Label htmlFor="live-status">Status line (shown under the score)</Label>
              <Input id="live-status" value={form.status_text} onChange={(e) => setForm({ ...form, status_text: e.target.value })} placeholder="Strikers need 24 off 18 balls" className="bg-white" data-testid="admin-live-status-text" />
            </div>
            <div>
              <Label htmlFor="live-stream">Stream URL — YouTube link or embed URL (plays on the Live page)</Label>
              <Input id="live-stream" value={form.stream_url} onChange={(e) => setForm({ ...form, stream_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" className="bg-white" data-testid="admin-live-stream-url" />
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold text-league-ink">
              <Checkbox checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} data-testid="admin-live-active" />
              Match is LIVE now — show the scoreboard on the Live page
            </label>
            <Button onClick={save} disabled={busy} data-testid="admin-live-save" className="gap-2">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save scoreboard
            </Button>
          </div>
        </Section>

        <Section title="Ball-by-ball log">
          <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-live-quick-balls">
            {QUICK_BALLS.map((b) => (
              <button
                key={b}
                onClick={() => addBall(b)}
                data-testid={`admin-ball-${b}`}
                className={`w-11 h-11 rounded-full font-oswald font-semibold text-sm transition-colors duration-150 ${
                  b === "W" ? "bg-league-red text-white hover:bg-league-ink" : b === "4" || b === "6" ? "bg-league-blue text-white hover:bg-league-deep" : "bg-league-mist text-league-ink hover:bg-league-blue hover:text-white"
                }`}
              >
                {b}
              </button>
            ))}
          </div>
          <div className="flex gap-2 mb-5">
            <Input value={customBall} onChange={(e) => setCustomBall(e.target.value)} placeholder="Custom (e.g. 1lb)" className="bg-white" data-testid="admin-live-custom-ball" />
            <Button variant="outline" onClick={() => { if (customBall.trim()) { addBall(customBall.trim()); setCustomBall(""); } }} data-testid="admin-live-custom-ball-add">Add</Button>
            <Button variant="outline" onClick={clearBalls} data-testid="admin-live-clear-balls" className="gap-2 text-league-red border-league-red/30">
              <Eraser size={14} /> Clear
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="admin-live-balls-list">
            {(match?.balls || []).map((b, i) => (
              <span key={i} className={`w-9 h-9 rounded-full flex items-center justify-center font-oswald text-xs font-semibold ${b === "W" ? "bg-league-red text-white" : b === "4" || b === "6" ? "bg-league-blue text-white" : "bg-league-mist text-league-ink"}`}>
                {b}
              </span>
            ))}
            {!(match?.balls || []).length && <p className="text-sm text-muted-foreground">No balls logged yet.</p>}
          </div>
          <p className="mt-5 text-xs text-muted-foreground">
            Public view: {match?.active ? "scoreboard is LIVE on /live" : match ? "scoreboard saved, showing as last result" : "no match yet — Live page shows the notify form"}
          </p>
        </Section>
      </div>
    </TabsContent>
  );
}
